import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity, ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AbaChart, ChartLegend } from "@/components/AbaChart";
import { SessionsTable } from "@/components/SessionsTable";
import { api } from "@/lib/api";
import { ageFromBirth, fmtDate, pct, trendLabel, deadlineLabel, deadlineClass } from "@/lib/aba";

export default function ReportPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["report", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}/report`)).data,
  });

  if (isLoading) {
    return <div className="min-h-screen bg-background p-8 max-w-[820px] mx-auto space-y-4"><Skeleton className="h-24" /><Skeleton className="h-80" /><Skeleton className="h-80" /></div>;
  }
  if (isError || !data) {
    return <div className="min-h-screen bg-background p-8 text-center text-destructive">Impossibile generare il report.</div>;
  }

  const { patient, totals, domains, periodo } = data;
  const age = ageFromBirth(patient.data_nascita);

  return (
    <div className="min-h-screen bg-muted/40 print:bg-white" data-testid="report-page">
      {/* Toolbar (hidden in print) */}
      <div className="no-print sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[820px] mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <Button variant="ghost" className="gap-2" onClick={() => navigate(`/pazienti/${patientId}`)} data-testid="report-back-button"><ArrowLeft className="h-4 w-4" /> Torna al paziente</Button>
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground hidden sm:block">Usa “Salva come PDF” nella finestra di stampa</p>
            <Button className="gap-2" onClick={() => window.print()} data-testid="report-print-button"><Printer className="h-4 w-4" /> Stampa / Salva PDF</Button>
          </div>
        </div>
      </div>

      <article className="report-sheet max-w-[820px] mx-auto bg-white text-slate-900 my-6 print:my-0 px-8 py-10 sm:px-12 rounded-xl shadow-sm print:shadow-none print:rounded-none border border-border print:border-0">
        {/* Header */}
        <header className="flex items-start justify-between gap-6 border-b border-slate-200 pb-6">
          <div>
            <div className="flex items-center gap-2 text-primary mb-3">
              <span className="h-8 w-8 rounded-lg bg-primary text-white flex items-center justify-center"><Activity className="h-4 w-4" /></span>
              <span className="font-heading font-semibold">ABA Clinica</span>
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight" data-testid="report-patient-name">Report dei progressi — {patient.nome} {patient.cognome}</h1>
            <p className="text-sm text-slate-600 mt-1">
              {age !== null && <>{age} anni (nato il {fmtDate(patient.data_nascita)}) · </>}
              {patient.diagnosi && <>{patient.diagnosi} · </>}
              Periodo: {periodo.da ? `${fmtDate(periodo.da)} – ${fmtDate(periodo.a)}` : "nessuna sessione registrata"}
            </p>
          </div>
          <div className="text-right text-xs text-slate-500 shrink-0">
            <p>Generato il {fmtDate(data.generato_il, "d MMMM yyyy")}</p>
            <p>Operatore: {data.generato_da}</p>
          </div>
        </header>

        {/* Family-friendly intro */}
        <section className="mt-6">
          <p className="text-sm leading-relaxed text-slate-700">
            Questo report riassume il lavoro svolto con {patient.nome} nelle diverse aree di apprendimento. Ogni obiettivo (“azione”) ha il proprio grafico:
            i punti mostrano la percentuale di risposte corrette in ogni sessione, la linea tratteggiata arancione indica il criterio di padronanza e la linea verticale blu
            segnala quando l'obiettivo è stato acquisito. Le lettere nei punti indicano il livello di aiuto fornito (V verbale, G gestuale, M modello, F fisico); un punto pieno significa risposta indipendente.
          </p>
          <div className="grid grid-cols-4 gap-3 mt-5">
            <Total label="Obiettivi" value={totals.azioni} />
            <Total label="Acquisiti" value={totals.acquisite} accent />
            <Total label="Sessioni" value={totals.sessioni} />
            <Total label="Media corrette" value={pct(totals.media)} />
          </div>
        </section>

        {domains.length === 0 && <p className="mt-10 text-center text-slate-500">Nessuna azione registrata per questo paziente.</p>}

        {domains.map((d) => (
          <section key={d.key} className="mt-10" data-testid={`report-domain-${d.key}`}>
            <div className="flex items-baseline justify-between border-b-2 border-primary/30 pb-2">
              <h2 className="font-heading text-xl font-semibold">{d.label}</h2>
              <span className="text-xs text-slate-500">{d.actions.length} {d.actions.length === 1 ? "obiettivo" : "obiettivi"}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{d.descrizione}</p>
            {d.actions.map(({ action, analytics }) => (
              <div key={action.action_id} className="report-block mt-6 rounded-xl border border-slate-200 p-5" data-testid={`report-action-${action.action_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{action.nome}</h3>
                    {action.descrizione && <p className="text-sm text-slate-600 mt-0.5">{action.descrizione}</p>}
                    {action.data_obiettivo && (() => { const dl = deadlineLabel(action); return dl ? <span className={`mt-1.5 inline-flex items-center text-[11px] font-medium border rounded px-1.5 py-0.5 ${deadlineClass(dl.tone)}`}>Obiettivo {fmtDate(action.data_obiettivo)} · {dl.text}</span> : null; })()}
                  </div>
                  <span className={`shrink-0 text-xs font-semibold rounded-md px-2 py-1 ${action.stato === "acquisito" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-sky-50 text-sky-800 border border-sky-200"}`}>
                    {action.stato === "acquisito" ? `Acquisito il ${fmtDate(action.data_acquisizione)}` : `In corso dal ${fmtDate(action.data_introduzione)}`}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-2 mt-4 text-center">
                  <Mini label="Sessioni" value={analytics.count} />
                  <Mini label="Ultima" value={pct(analytics.ultima)} />
                  <Mini label="Media" value={pct(analytics.media)} />
                  <Mini label="Tendenza" value={trendLabel(analytics.trend.direzione)} small />
                  <Mini label="Indipendenza" value={pct(analytics.percent_indipendente)} />
                </div>
                {analytics.count > 0 ? (
                  <div className="mt-4">
                    <AbaChart analytics={analytics} mode="percent" height={240} />
                    <div className="mt-2"><ChartLegend criterio={action.criterio} hasPhase={Boolean(analytics.cambio_fase_numero)} mode="percent" promptCounts={analytics.prompt_counts} /></div>
                    <div className="mt-4"><SessionsTable points={analytics.points} criterio={action.criterio} compact /></div>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-slate-500 italic">Nessuna sessione registrata per questo obiettivo.</p>
                )}
              </div>
            ))}
          </section>
        ))}

        <footer className="mt-12 pt-4 border-t border-slate-200 text-[11px] text-slate-500 flex justify-between">
          <span>ABA Clinica — report generato automaticamente dai dati di sessione.</span>
          <span>{patient.nome} {patient.cognome} · {fmtDate(data.generato_il)}</span>
        </footer>
      </article>
    </div>
  );
}

const Total = ({ label, value, accent }) => (
  <div className={`rounded-lg border p-3 ${accent ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-heading text-2xl font-semibold tabular">{value}</p>
  </div>
);

const Mini = ({ label, value, small }) => (
  <div className="rounded-lg bg-slate-50 py-2 px-1">
    <p className={`font-heading font-semibold tabular ${small ? "text-xs leading-6" : ""}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
  </div>
);
